import React, { memo } from 'react';
import styled from 'styled-components';
import { Checkbox, Tooltip } from 'antd';

import { Company } from 'Types/CompaniesTypes';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from 'Slice/CompaniesSlice';
import { actions as certificationActions } from 'Slice/CertificationSlice';
import { actions as flurryActions } from 'Slice/HelperSlice';
import classNames from 'classnames';
import { isOnboardingRoute, valueToObject } from 'Utils/UtilityFunctions';
import { COMPANY_OVERVIEW_ROUTE, translateSkipAreas } from 'Utils/Constants';
import { selectedSortingType } from 'Selector/CompaniesSelector';
import CompaniesSectionSkeleton from 'Components/company/components/FullComponentSkeleton/CompaniesSection';
import CompanyFollowUnFollowButton from './CompanyFollowUnfollowButton';
import {
  primaryColor, primaryFont, wMedium, maxWidth1170,
  maxWidth, retina, maxTablet, maxMobile, smMobile, maxWidth1300,
  textBlack,
  wMLight,
  wMBold,
  primaryHeadingColor,
  secondaryFont,
  minExtraLargeScreen,
} from '../../../Utils/utilsStyle';
import CompanyWidgetLarge from './CompanyWidgetLarge';
import WorldIcon from '../assets/svgIcons/WorldIcon';

type Props = {
  showButton?: boolean
  removeButton?: boolean
  removed?: boolean
  handleCompanyRemoved?: (remove?: boolean) => void,
  companyData?: Company,
  isSearch?: boolean,
  disableNavigation?: boolean,
  isCompanyCompare?: boolean,
  isTableCompanyCompare?: boolean,
  currency?: string,
  showDrawerIcon?: boolean
  showSapIcon?: boolean
  isRecomended?: boolean
  listView?: boolean,
  inGlobalSearch?: boolean,
  isDailyIQ?: boolean,
  /** When set, used for compare select instead of a per-render inline handleCompanyRemoved (stable for list perf). */
  onCompareSelectionToggle?: (companyId: number, selected: boolean) => void,
} & typeof defaultProps;

const defaultProps = {
  showButton: true,
  removeButton: false,
  removed: false,
  companyData: {} as Company,
  handleCompanyRemoved: () => { },
  isSearch: false,
  disableNavigation: false,
  isCompanyCompare: false,
  isTableCompanyCompare: false,
  currency: '',
  showDrawerIcon: false,
  showSapIcon: false,
  isRecomended: false,
  listView: false,
  inGlobalSearch: false,
  isDailyIQ: false,
};

const Wrapper = styled.div<{
  disabled: boolean,
  isCompanyComparePage: boolean,
  isTableCompany: boolean,
  isSearch: boolean,
}>`
  max-width: 205px;
  width: 205px;
  position: relative;
  cursor: pointer;

  display: ${(props) => (props.isCompanyComparePage ? 'flex' : 'inline-block')};
  justify-content: ${(props) => (props.isCompanyComparePage ? 'center' : 'initial')};
  align-items: ${(props) => (props.isCompanyComparePage ? 'center' : 'initial')};
  max-width: ${(props) => (props.isCompanyComparePage ? '100%' : '205px')};
  width: ${(props) => (props.isCompanyComparePage ? '100%' : '205px')};

  @media all and (max-width: ${maxWidth}) , ${retina} {
    max-width: ${(props) => (props.isCompanyComparePage ? '100%' : '162px')};
    width: ${(props) => (props.isCompanyComparePage ? '100%' : '162px')};
  }
  &.companiesSearch{ 
    .companyName {
      @media all and (max-width: ${smMobile}) {
        margin-bottom: 2px;
      }
    }
    .follow-action {
      .small-button-chinese {
        display: inline-flex;
        height: 24px;
        width: 84px;
        padding: 4px 14px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        border-radius: 16px;
        text-align: center;
        font-family: ${primaryFont};
        font-size: 14px;
        font-weight: ${wMLight};
        line-height: 17px;
        @media all and (min-width: ${minExtraLargeScreen}) {
          height: 34px;
          width: 120px;
          font-size: 20px;
          line-height: 23px;
          border-radius: 22px;
        }
        @media all and (max-width: ${maxWidth}) , ${retina} {
          height: 24px;
          width: 84px;
          border-radius: 16px;
          font-size: 14px;
          line-height: 17px;
          padding: 4px 14px;
        }
        @media all and (max-width: ${maxTablet}) {
          height: 20px;
          width: 72px;
          padding: 3px 12px;
          border-radius: 16px;
          font-size: 12px;
          line-height: 15px;
        }
        @media all and (max-width: ${maxMobile}) {
          height: 20px;
          width: 64px;
          font-size: 12px;
          line-height: 15px;
          padding: 3px 12px;
        }
      }
    }
    .exceptButtonContent {
      .company-image-parent {
        margin: 0px 0px 6px 0px;
      }
      .company-website-link-wrapper {
        @media all and (max-width: ${maxTablet}) {
          margin-bottom: 0px;
        }
        .company-website {
          margin-bottom: 4px;
          span {
            margin: 0px;
            max-width: 154px;
            color: ${primaryColor};
            font-family: ${primaryFont};
            font-size: 14px;
            font-weight: ${wMedium};
            line-height: 16px;
            letter-spacing: -0.315px;
            width: 88%;
            @media all and (min-width: ${minExtraLargeScreen}) {
              font-size: 16px;
              line-height: 18px;
              letter-spacing: -0.452px;
            }
            @media all and (max-width: ${maxWidth}) , ${retina} {
              font-size: 14px;
              line-height: 16px;
              letter-spacing: -0.315px;
            }
            @media all and (max-width: ${maxTablet}) {
              font-size: 12px;
              line-height: 14px;
              letter-spacing: -0.246px;
              max-width: 100px;
            }
            @media all and (max-width: ${maxMobile}) {
              font-size: 12px;
              line-height: 13.685px;
              letter-spacing: -0.309px;
            }
          }
        }
      }
    }
    .companyName {
      color: ${textBlack};
      text-align: center;
      font-family: ${primaryFont};
      font-size: 16px;
      font-weight: ${wMLight};
      line-height: 18px !important;
      letter-spacing: -0.52px;
      margin-bottom: 2px;
      min-height: 36px;
      @media all and (min-width: ${minExtraLargeScreen}) {
        font-size: 20px;
        line-height: 22px !important;
        letter-spacing: -0.746px;
      }
      @media all and (max-width: ${maxWidth}) , ${retina} {
        font-size: 16px;
        line-height: 18px !important;
        letter-spacing: -0.52px;
        margin-bottom:2px;
      }
      @media all and (max-width: ${maxTablet}) {
        font-size: 14px;
        line-height: 16px !important;
        letter-spacing: -0.414px;
        min-height: 32px;
      }
      @media all and (max-width: ${maxMobile}) {
        font-size: 14px;
        line-height: 18px !important;
        letter-spacing: -0.593px;
      }
    }
  }
  &.isCompanyTableItem {
    @media all and (max-width: ${smMobile}) {
      max-width: 100px;
    }
    .company-image-parent {
      width: 140px;
      height: 95px;
      @media all and (max-width: ${maxWidth1300}){
        width: 100px;
        height: 70px;
      }
      @media all and (max-width: ${maxMobile}){
        width: 70px;
        height: 40px;
        border-radius: 4px;
        padding: 2px;
      }
       
    }
    .logo-image-wrapper {
      @media all and (max-width: ${maxMobile}){
        border-radius: 4px;
      }
    }
    .progress-wrapper {
      @media all and (max-width: ${maxMobile}){
        bottom: 6px;
        padding: 0px 5px;
      }
      div {
        @media all and (max-width: ${maxMobile}){
          height: 2px;
        }
      }
    }
    .exceptButtonContent {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      a {
        transform: translateX(-5px);
      }
      .companyName {
        padding: 0px 10px;
       @media all and (max-width: ${maxWidth1300}){
        margin: 0 auto;
        padding: 0px;
       }
       @media all and (max-width: ${smMobile}){
        font-size: 12px;
        line-height: 14px;
       }
      }
      .company-stats  {
        display: none;
        p {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
    .small-button-chinese {
      @media all and (max-width: ${maxMobile}){
        padding: 5px 10px;
        font-size: 8px;
      }
    }
    .company-share  {
      @media all and (max-width: ${maxWidth1300}){
        flex-direction: column;
        .company-ticker-stats, p {
          margin-left: 0px;
          margin-bottom: 0px;
        }
        .seperator {
          display: none;
        }
      }
    }
    .actionTableBtn {
      button {
        margin: 10px 0px 0px 0px;
      }
    }
  }

  .follow-action {
    margin-top: 0px;
  }

  .exceptButtonContent {
    opacity: ${(props) => {
    if (props.disabled) return '0.5';
    return '1';
  }};
    svg{
      height: 10px;
      margin: 0px 0px 0px -11px;
      g{
      fill: #7E7E7E;
      }
    }
    /* .company-image-parent {
      margin: 0px 0px 10px 0px;
    } */
    .company-website-link-wrapper {
      min-height: 25px;
      display: flex;
      justify-content: center;
      @media all and (max-width: ${maxTablet}) {
        margin-bottom: 5px;
      }
      .company-website{
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 5px;
        position: relative;
        width: 100%;
        text-align: center;
        .website-tooltip {
          @media all and (max-width: ${maxTablet}) {
            width: 80%;
          }
          /* .ant-tooltip  {
            top: -46px !important;
            left: -15px !important;
          } */
        }
        svg {
          min-width: 16px;
          margin: 0px;
          margin-right: 4px;
	      }
        span{
          margin: 0px;
          max-width: 154px;
          font-size: 13px;
          font-weight: ${wMBold};
          font-family: ${primaryFont};
          color: ${primaryColor} !important;
        }
      }
    }
    .company-share{
      display: flex;
      align-items: center;
      justify-content: center;
      margin-top: 5px;
      .seperator{
        margin-top: -8px;
        font-size: 10px;
      }
    }
  }
  .companyName {
    font-size: 18px;
    color: ${primaryHeadingColor};
    font-weight: 800;
    font-family: ${secondaryFont};
    letter-spacing: -0.45px;
    line-height: 1.111 !important;
    
    @media all and (max-width: ${maxWidth}) , ${retina} {
      font-size: 13px;
      line-height: 22px !important;
      margin-bottom:2px;
    }
    @media all and (max-width: ${maxTablet}) {
      min-height: 18px;
    }
    @media all and (max-width: ${maxMobile}) {
      margin-left: 0px;
    }
  }
  p {
    font-size: 11px;
    color: #7E7E7E !important;
    font-weight: ${wMedium};
    font-family: ${primaryColor};
    font-family: ${primaryFont};
    letter-spacing: -0.75px;
    margin-left: 10px;
    margin-bottom: 5px;
    @media all and (max-width: ${maxWidth}) , ${retina} {
      font-size: 9px;
    }
  }
  .small-button-chinese {
    margin-bottom: 24px;
  }
  .deleteButton {
    position: absolute;
    top: -12px;
    right: -9px;
    width: fit-content;
    background-color: transparent;
    border: none;
    .ant-checkbox {
      &.ant-checkbox-checked {
        .ant-checkbox-inner {
          border: 2px solid ${primaryColor};
          background-color: none;
        }
      }
      .ant-checkbox-inner {
        width: 24px;
        height: 24px;
        border: 2px solid #C1C1C1;
        margin: 0px;
        @media all and (max-width: ${maxWidth1170}) {
          height: 22px;
          width: 22px;
        }
      }
    }
    :hover {
      .ant-checkbox-checked {
        .ant-checkbox-inner {
          background-color: white !important;
          border: 2px solid ${primaryColor} !important;
        }
      }
    }
  }
  .check_label{
    cursor: pointer;
  }
  .actionTableBtn {
    button {
      padding: 5px 10px;
      font-size: 10px;
      margin: 10px 0px 0px 10px;
      @media all and (max-width: ${maxTablet}) {
        margin: 10px 0px 0px 0px;
      }
    }
  }
`;

const CompanyCard = function CompanyCard(props: Props) {
  const {
    showButton,
    removeButton,
    removed,
    companyData,
    handleCompanyRemoved,
    isSearch,
    disableNavigation,
    isCompanyCompare,
    isTableCompanyCompare,
    currency,
    showDrawerIcon,
    showSapIcon,
    isRecomended,
    listView,
    inGlobalSearch,
    isDailyIQ,
    onCompareSelectionToggle,
  } = props;
  const url = `${COMPANY_OVERVIEW_ROUTE}${companyData.companyId}`;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const host = window.location.href;
  const sortingType = useSelector(selectedSortingType);

  const isOnboarding = isOnboardingRoute();

  const handleCardClicked = () => {
    dispatch(actions.setNewsFlurry({}));
    if (!removeButton && !disableNavigation) navigate(`${COMPANY_OVERVIEW_ROUTE}${companyData.companyId}`);
  };

  const handleCardRightClicked = () => {
    dispatch(actions.setNewsFlurry({}));
    if (!removeButton && !disableNavigation) window.open(`${COMPANY_OVERVIEW_ROUTE}${companyData.companyId}, '_blank'`);
  };

  const openCompanyWindow = () => {
    window.open(process.env.REACT_APP_CALLBACK_URL ? process.env.REACT_APP_CALLBACK_URL + url : '');
  };

  const {
    companyLogo, companyPersonality, companyId, companyDomain, companyWebsite,
    companyName, companyPulse, companySharePrice, companyTicker,
    companySubscribers, companyRevenue,
  } = companyData;

  const handleCompanyWebsite = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent> | React.KeyboardEvent<HTMLSpanElement>,
  ) => {
    e.stopPropagation();
    window.open(companyWebsite.slice(0, 4) === 'http' ? companyWebsite : `https://${companyWebsite}`);
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('company_websiteLink_clicked', 0, {
        company_id: companyId,
        company_name: companyName,
        host,

      }),
    ));
  };

  const handleClick = () => {
    if (isSearch && !disableNavigation) {
      dispatch(certificationActions.setSearchCompanyClicked(true));
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('searchPage_company_profile_clicked', 0, {
          company_id: companyId,
          company_name: companyName,
          host,
        }),
      ));
    }
  };

  const handleOnClick = () => {
    if (isTableCompanyCompare) {
      openCompanyWindow();
    } else {
      handleCardClicked();
    }
  };

  const handleInfoClick = () => {
    if (!isDailyIQ) {
      navigate(url);
    }
  };
  return (
    <Wrapper
      disabled={!isDailyIQ && removeButton}
      onClick={handleOnClick}
      className={classNames('company-item', { isCompanyTableItem: isTableCompanyCompare, companiesSearch: inGlobalSearch })}
      isCompanyComparePage={isCompanyCompare}
      isTableCompany={isTableCompanyCompare}
      isSearch={isSearch}
    >
      <label className="check_label" htmlFor={`${companyId}`}>
        <div className="exceptButtonContent">
          <span
            // style this class
            role="button"
            aria-label="view  profile"
            tabIndex={0}
            onClick={handleClick}
            onKeyPress={handleClick}
          >
            <CompanyWidgetLarge
              logo={companyLogo}
              persona={companyPersonality}
              triggerColor={companyPulse}
              imagePosition="center 35%"
              imageSize="50%"
              imageSizeSm="35%"
              heightXsm="112px"
              widthXsm={isCompanyCompare ? '100%' : '105px'}
              widthSm="160px"
              heightSm="112px"
              widthXxsm="100%"
              heightXxsm="100px"
              showDrawerIcon={showDrawerIcon}
              showSapIcon={showSapIcon}
              id={companyId}
              handleCardClicked={handleCardRightClicked}
              removeButton={removeButton}
              listView={listView}
              inGlobalSearch={inGlobalSearch}
            />
          </span>
          <div className="company-naves">
            <span
              role="button"
              tabIndex={0}
              onClick={handleInfoClick}
              onKeyPress={handleInfoClick}
            >
              <Tooltip
                trigger="hover"
                placement="top"
                getPopupContainer={(e: HTMLElement) => e}
                className="triggers-bullet-tooltip person-designation-tip"
                color="#fff"
                title={companyName}
                autoAdjustOverflow={false}
                arrow={false}
              >
                <h5 className="primary-color companyName text-center text-truncate">
                  {companyName}
                </h5>
              </Tooltip>
              <div className={`company-stats ${isSearch || isCompanyCompare ? 'company-share' : 'd-flex align-items-center w-100 justify-content-center'}`}>
                {
                  !(sortingType === 'revenue:asc' || sortingType === 'revenue:desc') && companySharePrice?.length && companySharePrice !== 'N/A' ? (
                    <>
                      <p className="company-ticker-stats text-truncate">
                        {`${companyTicker}: ${currency === '' ? '$' : currency}${parseFloat(companySharePrice).toFixed(2)}`}
                      </p>
                      <span className="seperator">|</span>
                    </>
                  ) : null
                }
                <p className="text-truncate">
                  {
                    ((sortingType === 'revenue:asc' || sortingType === 'revenue:desc') && isRecomended)
                      ? `${companyRevenue !== undefined && companyRevenue !== null ? `Rev: ${companyRevenue}` : '$'}`
                      : `${companySubscribers !== undefined && companySubscribers !== null ? companySubscribers : 0} followers`
                  }
                </p>
              </div>

            </span>
            <div className="company-website-link-wrapper">
              {companyDomain
                ? (
                  <div className="company-website">
                    <Tooltip
                      trigger="hover"
                      placement="top"
                      getPopupContainer={(e: HTMLElement) => e}
                      className="triggers-bullet-tooltip person-designation-tip website-tooltip"
                      color="#fff"
                      title={companyDomain}
                      arrow={false}
                      align={{ offset: [0, 20] }}
                    >
                      <span
                        className={`text-truncate ${translateSkipAreas}`}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => handleCompanyWebsite(e)}
                        onKeyPress={(e) => handleCompanyWebsite(e)}
                      >
                        {companyDomain}
                      </span>
                    </Tooltip>
                  </div>
                ) : null}
            </div>
            {
              showButton
                ? (
                  <div className={classNames('d-flex justify-content-center follow-action', { actionTableBtn: isTableCompanyCompare })}>
                    <CompanyFollowUnFollowButton
                      removeButton={removeButton}
                      companyData={companyData}
                      isSearch={isSearch}
                    />
                  </div>
                ) : null
            }
          </div>
        </div>
        {
          removeButton
            ? (
              <Checkbox
                id={`${companyId}`}
                checked={removed}
                className="deleteButton"
                onClick={() => {
                  if (onCompareSelectionToggle) {
                    onCompareSelectionToggle(companyId, !removed);
                  } else {
                    handleCompanyRemoved(!removed);
                  }
                }}
              />
            )
            : null
        }
      </label>
    </Wrapper>
  );
};

CompanyCard.defaultProps = defaultProps;
export default memo(CompanyCard);